import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ApiDataBase from "server";
import UseContext from "hooks/useContext";
import SectionWrapper, {
    FormField,
} from "pages/createMission/components/SectionWrapper";
import MonoSelect from "components/componentsForm/monoSelect";
import {
    templateIntitulePrefix,
    getTemplateIntituleSuffix,
    isTemplateNameTaken,
} from "pages/missionTemplates/utils/templateIntitule";

const QUALIFICATION_TYPES = {
    sale: { name: "Vente", icon: "🛍" },
    food: { name: "Restauration", icon: "🍳" },
    logistic: { name: "Logistique", icon: "📦" },
    administration: { name: "Administration", icon: "🧾" },
    largeDistribution: { name: "Grande distribution", icon: "🛒" },
    industry: { name: "Industrie", icon: "🏭" },
    other: { name: "Autre", icon: "👷" },
};

const ActivityTemplate = () => {
    const { id } = useParams();
    const { templateData, setTemplateData, missionTemplates } = UseContext();
    const [typesQualifications, setTypesQualifications] = useState([]);
    const [isWaitingTypes, setIsWaitingTypes] = useState(true);
    const [listQualifications, setListQualifications] = useState([]);
    const namePrefix = templateIntitulePrefix(templateData.qualification);

    /** false = on peut encore recalculer le préfixe quand la qualification change (création). */
    const [hasTouchedName, setHasTouchedName] = useState(() => {
        const auto = templateIntitulePrefix(templateData.qualification);
        const stored = templateData.name || "";
        if (!stored.trim()) return false;
        if (!auto) return true;
        return stored !== auto;
    });

    // The wizard only holds the name the user is typing: the conflict is read
    // from the templates already listed for the company.
    const isNameTaken = isTemplateNameTaken(
        templateData.name,
        missionTemplates,
        id
    );

    const selectedType = Object.keys(QUALIFICATION_TYPES).find(
        (key) => QUALIFICATION_TYPES[key].name === templateData.activity
    );

    const filteredQualifications = selectedType
        ? listQualifications.filter((q) => q.type === selectedType)
        : [];

    useEffect(() => {
        ApiDataBase.getListQualifications(
            {},
            (res) => setListQualifications(res),
            (err) => console.warn(err)
        );
    }, []);

    useEffect(() => {
        setIsWaitingTypes(true);
        ApiDataBase.getTypesQualifications(
            {},
            (res) => {
                setTypesQualifications(res);
                setIsWaitingTypes(false);
            },
            (err) => {
                console.warn(err);
                setIsWaitingTypes(false);
            }
        );
    }, []);

    const handleTypeClick = (type) => {
        const clearing = selectedType === type;
        setTemplateData((prev) => ({
            ...prev,
            activity:
                clearing ? "" : QUALIFICATION_TYPES[type].name,
            qualification: clearing ? "" : prev.qualification,
            ...(clearing && !hasTouchedName ? { name: "" } : {}),
        }));
    };

    const handleQualificationChoice = (selected) => {
        const value = selected ? selected.value : "";
        setTemplateData((prev) => {
            if (!value) {
                return { ...prev, qualification: "", name: "" };
            }
            const suffix = getTemplateIntituleSuffix(prev.name, prev.qualification);
            return {
                ...prev,
                qualification: value,
                name: templateIntitulePrefix(value) + suffix,
            };
        });
    };

    const handleSuffixChange = (e) => {
        setHasTouchedName(true);
        const suffix = e.target.value;
        setTemplateData((prev) => ({
            ...prev,
            name: templateIntitulePrefix(prev.qualification) + suffix,
        }));
    };

    return (
        <div className={"flex flex-col gap-6"}>
            <SectionWrapper
                title="Secteur d'activité"
                description="Sélectionnez le secteur d'activité et la qualification recherchée pour cette fiche de poste."
            >
                <FormField label="Secteur">
                    <div className={"grid grid-cols-3 gap-4"}>
                        {isWaitingTypes
                            ? [1, 2, 3, 4, 5].map((i) => (
                                  <div
                                      key={i}
                                      className={
                                          "h-24 flex flex-col justify-center items-center gap-2 border rounded-lg"
                                      }
                                  >
                                      <div className={"h-8 bg-gray-200 rounded w-8"} />
                                      <div className={"h-3 bg-gray-200 rounded w-20"} />
                                  </div>
                              ))
                            : typesQualifications.map(
                                  (type) =>
                                      QUALIFICATION_TYPES[type] && (
                                          <div
                                              key={type}
                                              className={`h-24 flex flex-col justify-center items-center gap-2 border rounded-lg cursor-pointer ${
                                                  selectedType === type
                                                      ? "border-yellow-500 bg-yellow-50"
                                                      : "border-gray-200"
                                              }`}
                                              onClick={() => handleTypeClick(type)}
                                          >
                                              <div className={"text-2xl"}>
                                                  {QUALIFICATION_TYPES[type].icon}
                                              </div>
                                              <div className={"ft-sm"}>
                                                  {QUALIFICATION_TYPES[type].name}
                                              </div>
                                          </div>
                                      )
                              )}
                    </div>
                </FormField>
                <FormField label="Qualification">
                    <MonoSelect
                        data={filteredQualifications}
                        selectArray={handleQualificationChoice}
                        defaultValue={templateData.qualification}
                    />
                </FormField>
            </SectionWrapper>
            <SectionWrapper
                title="Nom du modèle"
                description="L'intitulé de la mission (qualification suivie de « - ») est fixé automatiquement. Vous pouvez uniquement compléter après le tiret (lieu, précision, etc.)."
            >
                <FormField label="Nom">
                    <div
                        className={`flex w-full min-h-10 rounded-lg border overflow-hidden ${
                            !templateData.qualification?.trim()
                                ? "border-gray-200 bg-gray-50 opacity-70"
                                : isNameTaken
                                ? "border-red-500"
                                : "border-gray-300"
                        }`}
                    >
                        <span
                            className="shrink-0 max-w-[55%] px-3 py-2 text-sm leading-5 text-gray-800 bg-gray-100 border-r border-gray-200 truncate"
                            title={namePrefix || undefined}
                        >
                            {namePrefix || "—"}
                        </span>
                        <input
                            type="text"
                            className="flex-1 min-w-0 h-10 pl-3 pr-3 ft-b placeholder-gray-600 border-0 focus:outline-none focus:ring-0 disabled:bg-gray-50"
                            value={getTemplateIntituleSuffix(
                                templateData.name,
                                templateData.qualification
                            )}
                            onChange={handleSuffixChange}
                            placeholder={
                                namePrefix
                                    ? "Complément"
                                    : "Choisissez d'abord une qualification"
                            }
                            disabled={!templateData.qualification?.trim()}
                        />
                    </div>
                    {isNameTaken && (
                        <p className="mt-1 ft-sm text-red-500">
                            Une fiche de poste porte déjà ce nom.
                        </p>
                    )}
                </FormField>
            </SectionWrapper>
        </div>
    );
};

export default ActivityTemplate;
